import { Building, FileText } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { Tab } from '@headlessui/react';

import Input from '../Forms/Input';
import Select from '../Forms/Select';
import Textarea from '../Forms/Textarea';
import { Button } from '../Common/button';
import { UseCaseSummary, UseCaseStatus } from '../../types';
import useCaseService from '@/services/useCaseService';
import cadrageService from '@/services/cadrageService';
import notify from '../../lib/notify';

interface FormData {
  name: string;
  client: string;
  company_name?: string;
  company_sector?: string;
  company_size?: string;
  company_revenue?: string;
  company_location?: string;
  main_contact_name?: string;
  main_contact_role?: string;
  main_contact_email?: string;
  main_contact_phone?: string;
  useCaseSelection: string;
  newUseCaseName?: string;
  newUseCaseDescription?: string;
}

interface NewCadrageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  preselectedUseCaseId?: string;
  preselectedUseCaseName?: string;
}

export const NewCadrageModal: React.FC<NewCadrageModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  preselectedUseCaseId,
  preselectedUseCaseName,
}) => {
  const { t } = useTranslation();
  const [existingUseCases, setExistingUseCases] = useState<UseCaseSummary[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [preselectedUseCaseDisplayName, setPreselectedUseCaseDisplayName] = useState(
    preselectedUseCaseName || ''
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm<FormData>({
    defaultValues: {
      name: '',
      client: '',
      company_name: '',
      company_sector: '',
      company_size: '',
      company_revenue: '',
      company_location: '',
      main_contact_name: '',
      main_contact_role: '',
      main_contact_email: '',
      main_contact_phone: '',
      useCaseSelection: preselectedUseCaseId || 'new',
      newUseCaseName: '',
      newUseCaseDescription: '',
    },
  });

  const useCaseSelectionValue = useWatch({
    control,
    name: 'useCaseSelection',
  });
  const isCreatingNewUseCase = useCaseSelectionValue === 'new';

  // Fetch available use cases (only if no preselected use case)
  useEffect(() => {
    if (!isOpen) return;
    if (preselectedUseCaseId) return; // Skip if use case is preselected

    const fetchAvailableUseCases = async () => {
      try {
        const useCases = await useCaseService.list({ available_for_cadrage: true });
        const useCasesList = useCases.map((uc: any) => ({
          id: uc.id,
          name: uc.name,
          status: uc.status,
          short_description: uc.short_description,
          value_score: uc.value_score,
          complexity_score: uc.complexity_score,
        }));

        setExistingUseCases(useCasesList);
      } catch (error) {
        console.error('Error fetching available use cases:', error);
        setExistingUseCases([]);
      }
    };

    fetchAvailableUseCases();
  }, [isOpen, preselectedUseCaseId]);

  // Fetch the preselected use case name if not provided
  useEffect(() => {
    if (!isOpen) return;
    if (!preselectedUseCaseId) return;
    if (preselectedUseCaseDisplayName) return; // Already have the name

    const fetchUseCaseName = async () => {
      try {
        const useCase = await useCaseService.get(preselectedUseCaseId);
        setPreselectedUseCaseDisplayName(useCase.name);
      } catch (error) {
        console.error('Error fetching preselected use case:', error);
        setPreselectedUseCaseDisplayName(t('global.notAvailable'));
      }
    };

    fetchUseCaseName();
  }, [isOpen, preselectedUseCaseId, preselectedUseCaseDisplayName, t]);

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    setIsSubmitting(true);
    let sourceUseCaseId: string | undefined = undefined;

    try {
      // Handle use case creation or selection
      if (preselectedUseCaseId) {
        // Use the preselected use case
        sourceUseCaseId = preselectedUseCaseId;
      } else if (data.useCaseSelection === 'new') {
        if (!data.newUseCaseName || !data.newUseCaseDescription) {
          notify.error(t('newCadrage.missingDataMessage'));
          setIsSubmitting(false);
          return;
        }

        const newUseCasePayload = {
          name: data.newUseCaseName,
          description: data.newUseCaseDescription,
          short_description:
            data.newUseCaseDescription.substring(0, 100) +
            (data.newUseCaseDescription.length > 100 ? '...' : ''),
          status: 'Identified' as UseCaseStatus,
        };

        try {
          const newUseCase = await useCaseService.create(newUseCasePayload);
          sourceUseCaseId = newUseCase.id;
        } catch (error) {
          console.error('Error creating use case:', error);
          notify.error(t('newCadrage.useCaseCreationErrorMessage'));
          setIsSubmitting(false);
          return;
        }
      } else {
        sourceUseCaseId = data.useCaseSelection;
      }

      if (!sourceUseCaseId) {
        notify.error(t('newCadrage.useCaseMissingMessage'));
        setIsSubmitting(false);
        return;
      }

      // Create the cadrage
      const cadragePayload = {
        name: data.name,
        type: 'cadrage',
        use_case_id: sourceUseCaseId,
        company_name: data.company_name || null,
        company_sector: data.company_sector || null,
        company_size: data.company_size || null,
        company_revenue: data.company_revenue || null,
        company_location: data.company_location || null,
        main_contact_name: data.main_contact_name || null,
        main_contact_role: data.main_contact_role || null,
        main_contact_email: data.main_contact_email || null,
        main_contact_phone: data.main_contact_phone || null,
      } as const;

      const createdCadrage = await cadrageService.createCadrage(cadragePayload);
      notify.show({
        variant: 'success',
        title: t('newCadrage.success'),
        description: createdCadrage?.name ? `"${createdCadrage.name}" ${t('newCadrage.successCreated', 'has been created.')}` : undefined,
        link: createdCadrage?.id ? `/cadrages/${createdCadrage.id}` : undefined,
      });
      reset();
      onSuccess();
    } catch (error) {
      console.error('Error creating cadrage:', error);
      notify.error(t('newCadrage.cadrageCreationErrorMessage'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={handleClose} />
      <div className="relative rounded-xl border border-border bg-card w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <FileText className="h-6 w-6 text-primary" />
            <h3 className="text-lg font-semibold text-foreground">{t('newCadrage.title')}</h3>
          </div>
          <button
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Tabs Content */}
        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit(onSubmit)} className="h-full">
            <Tab.Group>
              <Tab.List className="flex space-x-2 bg-muted p-1 m-4 rounded-lg">
                {[
                  { label: t('newCadrage.tabs.overview'), icon: FileText },
                  { label: t('newCadrage.tabs.companyInfo'), icon: Building },
                ].map(({ label, icon: Icon }) => (
                  <Tab
                    key={label}
                    className={({ selected }) =>
                      `flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors flex items-center justify-center gap-2
                      ${
                        selected
                          ? 'bg-background text-foreground shadow'
                          : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                      }`
                    }
                  >
                    <Icon className="h-4 w-4" />
                    <span>{label}</span>
                  </Tab>
                ))}
              </Tab.List>

              <Tab.Panels className="px-6 pb-6">
                {/* Overview Tab */}
                <Tab.Panel className="space-y-4">
                  <div className="space-y-4">
                    <Input
                      label={t('newCadrage.nameLabel')}
                      name="name"
                      register={register}
                      rules={{ required: t('newCadrage.nameRequired') }}
                      error={errors.name}
                    />
                    <Input
                      label={t('newCadrage.clientLabel')}
                      name="client"
                      register={register}
                      error={errors.client}
                    />
                  </div>

                  <div className="space-y-4 pt-4">
                    <h4 className="text-base font-semibold text-foreground">
                      {t('newCadrage.associatedUseCase')}
                    </h4>
                    
                    {preselectedUseCaseId ? (
                      <Input
                        label={t('newCadrage.useCaseLabel')}
                        name="useCaseDisplay"
                        value={preselectedUseCaseDisplayName}
                        disabled={true}
                        register={register}
                      />
                    ) : (
                      <>
                        <Select
                          label={t('newCadrage.selectOrCreate')}
                          name="useCaseSelection"
                          register={register}
                          error={errors.useCaseSelection}
                          options={[
                            { value: 'new', label: t('newCadrage.createNew') },
                            ...existingUseCases.map((uc) => ({ value: uc.id, label: uc.name })),
                          ]}
                        />

                        {isCreatingNewUseCase && (
                          <div className="mt-4 p-4 bg-muted/50 rounded-lg border border-border space-y-4">
                            <p className="text-sm text-muted-foreground">
                              {t('newCadrage.newUseCaseDetails')}
                            </p>
                            <Input
                              label={t('newCadrage.newUseCaseNameLabel')}
                              name="newUseCaseName"
                              register={register}
                              rules={{
                                required: isCreatingNewUseCase
                                  ? t('newCadrage.newUseCaseNameRequired')
                                  : false,
                              }}
                              error={errors.newUseCaseName}
                            />
                            <Textarea
                              label={t('newCadrage.newUseCaseDescriptionLabel')}
                              name="newUseCaseDescription"
                              register={register}
                              rows={4}
                              rules={{
                                required: isCreatingNewUseCase
                                  ? t('newCadrage.newUseCaseDescriptionRequired')
                                  : false,
                              }}
                              error={errors.newUseCaseDescription}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </Tab.Panel>

                {/* Company Info Tab */}
                <Tab.Panel className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    {t('newCadrage.companyInfoOptional')}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label={t('newCadrage.companyNameLabel')}
                      name="company_name"
                      register={register}
                      error={errors.company_name}
                    />
                    <Input
                      label={t('newCadrage.companySectorLabel')}
                      name="company_sector"
                      register={register}
                      error={errors.company_sector}
                    />
                    <Input
                      label={t('newCadrage.companySizeLabel')}
                      name="company_size"
                      register={register}
                      error={errors.company_size}
                    />
                    <Input
                      label={t('newCadrage.companyRevenueLabel')}
                      name="company_revenue"
                      register={register}
                      error={errors.company_revenue}
                    />
                    <Input
                      label={t('newCadrage.companyLocationLabel')}
                      name="company_location"
                      register={register}
                      error={errors.company_location}
                      className="md:col-span-2"
                    />
                  </div>

                  <div className="pt-4">
                    <h4 className="text-base font-medium text-foreground mb-4">
                      {t('newCadrage.mainContact')}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        label={t('newCadrage.contactNameLabel')}
                        name="main_contact_name"
                        register={register}
                        error={errors.main_contact_name}
                      />
                      <Input
                        label={t('newCadrage.contactRoleLabel')}
                        name="main_contact_role"
                        register={register}
                        error={errors.main_contact_role}
                      />
                      <Input
                        label={t('newCadrage.contactEmailLabel')}
                        name="main_contact_email"
                        type="email"
                        register={register}
                        error={errors.main_contact_email}
                      />
                      <Input
                        label={t('newCadrage.contactPhoneLabel')}
                        name="main_contact_phone"
                        type="tel"
                        register={register}
                        error={errors.main_contact_phone}
                      />
                    </div>
                  </div>
                </Tab.Panel>
              </Tab.Panels>
            </Tab.Group>
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex justify-end gap-3 flex-shrink-0">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
            {t('global.cancel')}
          </Button>
          <Button type="submit" onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
            {isSubmitting ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {t('global.creating')}
              </div>
            ) : (
              t('newCadrage.create')
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
